/**
 * THE phone rule of the app (owner, 27 Sep 2026: "سيب المفتاح للدولة فى الجميع" - every phone keeps its
 * country code, one format everywhere). The engine's twin is engine/fines_pipeline/firestore_apply.py
 * phone_digits - both pass the same table (phoneUtils.test.js / tests/test_phones.py).
 *
 * phoneDigits(raw) -> international digits without '+', '' when there is no usable phone:
 *   Ejar's "code-number":  "966-540550044" -> "966540550044"; its slips are mended:
 *                          "966-0544765122" (0 after the code), "966-+966546644513" / "966-00966543051332"
 *                          (code typed twice), "9-66555150390" (split in the wrong place)
 *   already international: "+971 50 123 4567", "00966501112233", "966501112233" (10+ digits) -> as they are
 *   local (no code):       "0501234567" / "501234567" -> 971...; 053 / 057 / 059 are Saudi-only -> 966...;
 *                          a UAE landline "042345678" -> 97142345678
 * formatPhone(digits) -> "+966540550044" (how every page shows it); whatsappUrl(digits[, text]) -> wa.me link,
 *                         the chat opened with `text` already typed when one is given.
 */
const ARABIC_DIGITS = '٠١٢٣٤٥٦٧٨٩';
const PERSIAN_DIGITS = '۰۱۲۳۴۵۶۷۸۹';
const GULF = /^(971|966|965|973|974|968)(\d{7,})$/;

const westernDigits = (text) => String(text).replace(/[٠-٩۰-۹]/g, (c) => {
    const i = ARABIC_DIGITS.indexOf(c);
    return String(i >= 0 ? i : PERSIAN_DIGITS.indexOf(c));
});

/** A number written without a country code: the UAE by default, Saudi for its own mobile ranges. */
const localNumber = (digits) => {
    const n = digits.replace(/^0/, '');
    if (n.length === 9 && n.startsWith('5')) return (/^5[379]/.test(n) ? '966' : '971') + n;
    if (n.length === 8 && /^[234679]/.test(n)) return `971${n}`;
    return digits;
};

export const phoneDigits = (raw) => {
    if (raw === null || raw === undefined) return '';
    const text = westernDigits(raw).trim();
    const dash = text.match(/^\s*\+?\s*(?:00)?(\d{1,4})\s*-\s*(.+)$/);
    if (dash && !dash[1].startsWith('0')) {
        let code = dash[1];
        let number = dash[2].replace(/\D/g, '').replace(/^00/, '');
        if (number.startsWith(code) && number.length - code.length >= 7) number = number.slice(code.length);
        number = number.replace(/^0+/, '');
        const joined = (code + number).match(GULF);
        if (code.length <= 2 && joined) [code, number] = [joined[1], joined[2].replace(/^0+/, '')];
        return number.length >= 7 ? code + number : '';
    }
    const digits = text.replace(/\D/g, '');
    if (!digits) return '';
    if (digits.startsWith('00')) return digits.slice(2).length >= 7 ? digits.slice(2) : '';
    if (digits.startsWith('0')) return localNumber(digits);
    if (digits.length >= 10) return digits;
    return localNumber(digits);
};

/** Old name, same rule: it used to drop the country code (and turned any long number starting with 9 into its
 *  last 9 digits, so a Saudi 966532052000 read as a UAE 532052000) - now it keeps it. */
export const cleanPhoneNumber = phoneDigits;

export const formatPhone = (digits) => (digits ? `+${digits}` : '');
export const whatsappUrl = (digits, text) => `https://wa.me/${digits}${text ? `?text=${encodeURIComponent(text)}` : ''}`;
